import React from 'react';
import { X, GripVertical } from 'lucide-react';
import { Condition } from '../../hooks/useMultiFilter';
import { CustomSelect } from '../common/CustomSelect';
import { getOperatorsForFieldType, inferFieldType, operatorRequiresValue } from '../../utils/filterOperators';

export interface FilterField {
  id: string;
  name: string;
  type?: string;
  icon?: React.ComponentType<{ style?: React.CSSProperties }>;
}

export interface FilterConditionRowProps {
  condition: Condition;
  index: number;
  fields: FilterField[];
  isDragging: boolean;
  showDropIndicator: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onUpdate: (updates: Partial<Condition>) => void;
  onRemove: () => void;
  getOperatorsForField: (fieldId: string) => { value: string; label: string }[];
}

export const FilterConditionRow: React.FC<FilterConditionRowProps> = ({
  condition,
  index,
  fields,
  isDragging,
  showDropIndicator,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onUpdate,
  onRemove,
  getOperatorsForField,
}) => {
  const needsValue = operatorRequiresValue(condition.operator);

  return (
    <div className="relative">
      {showDropIndicator && (
        <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-blue-500 rounded-full z-10" />
      )}
      <div
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        className={`flex items-center gap-2 transition-opacity ${isDragging ? 'opacity-50' : ''}`}
      >
        {/* Drag handle */}
        <button
          aria-label={`Reorder condition ${index + 1}`}
          className="flex items-center justify-center w-6 h-8 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-50 cursor-grab transition-all flex-shrink-0"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>

        {/* Combiner (AND/OR) - only for rows 2+ */}
        {index > 0 && (
          <div className="w-16 flex-shrink-0">
            <CustomSelect
              value={condition.combiner || 'AND'}
              onChange={(value) => onUpdate({ combiner: value as 'AND' | 'OR' })}
              options={[
                { value: 'AND', label: 'AND' },
                { value: 'OR', label: 'OR' }
              ]}
              aria-label="Condition combiner"
            />
          </div>
        )}

        {/* Field select */}
        <div className="flex-1 min-w-[160px]">
          <CustomSelect
            value={condition.fieldId}
            onChange={(value) => {
              const selectedField = fields.find(f => f.id === value);
              if (selectedField) {
                const fieldType = inferFieldType(selectedField.name, selectedField.type);
                const operators = getOperatorsForFieldType(fieldType);
                const defaultOperator = operators[0]?.value;

                onUpdate({
                  fieldId: value,
                  operator: defaultOperator,
                  value: undefined,
                  error: undefined
                });
              }
            }}
            options={[
              { value: '', label: 'Select field...' },
              ...fields.map(field => ({ value: field.id, label: field.name }))
            ]}
            placeholder="Select field..."
            aria-label={`Field for condition ${index + 1}`}
          />
        </div>

        {/* Operator select */}
        <div className="w-32 flex-shrink-0">
          <CustomSelect
            value={condition.operator}
            onChange={(value) => {
              const valueNeeded = operatorRequiresValue(value as any);
              onUpdate({
                operator: value as any,
                value: valueNeeded ? condition.value : undefined,
                error: valueNeeded && !condition.value ? 'Required' : undefined
              });
            }}
            options={condition.fieldId ? getOperatorsForField(condition.fieldId) : [{ value: 'contains', label: 'Contains' }]}
            aria-label={`Operator for condition ${index + 1}`}
            disabled={!condition.fieldId}
          />
        </div>

        {/* Value input */}
        <input
          type="text"
          value={condition.value || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            onUpdate({ value: newValue, error: newValue ? undefined : 'Required' });
          }}
          placeholder={needsValue ? "Enter value..." : "—"}
          aria-label={`Value for condition ${index + 1}`}
          disabled={!needsValue}
          className={`flex-1 min-w-[120px] h-8 px-3 text-[13px] font-medium rounded-lg border outline-none transition-all ${
            !needsValue
              ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
              : condition.error
                ? 'bg-white text-gray-900 border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                : 'bg-white text-gray-900 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
          }`}
        />

        {/* Remove button */}
        <button
          onClick={onRemove}
          aria-label={`Remove condition ${index + 1}`}
          className="flex items-center justify-center w-6 h-8 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
